export const sourceTypes = {
  database: "database",
  document: "document",
} as const;

export const chatTypes = {
  qa: "QA",
  conversation: "CONVERSATION",
} as const;

export const ROLE_NAMES = {
  systemAdmin: "SystemAdmin",
  orgAdmin: "OrgAdmin",
  groupAdmin: "GroupAdmin",
  user: "User",
} as const;

export const LLM_DEFAULT_TYPE = { Nvidia: "Nvidia" } as const;

export const SOURCE_TYPE_OPTIONS = ["Document", "Web URL"] as const;
export const STATUS_TYPE_OPTIONS = ["FAILED", "PROCESSING", "SUCCESS"] as const;
export const CHAT_TYPE_OPTIONS = ["QA", "CONVERSATION"] as const;
export const ROLES_OPTIONS = ["IDB_USERS", "IDB_ADMINS"] as const;
export const super_admin = "Super Admin";

export const MESSAGE_SENDER = {
  USER: "user",
  AI: "ai",
} as const;

export const languages = {
  language: "Language",
  english: "English",
  french: "French",
};

export const languageLabels = {
  english: "EN",
  french: "FR",
};

export const KNOWLEDGE_BASES = {
  GENIE: {
    id: "genie",
    name: "Genie",
  },
  JOURNEY: {
    id: "onboarding-journey",
    name: "Journey",
  },
  BANKING_AGENTS: {
    id: "banking-agents",
    name: "Banking Agents",
  },
} as const;

/**
 * Trend types
 */
export const METRIC_TREND = {
  UP: "UP",
  DOWN: "DOWN",
  STABLE: "STABLE",
} as const;

/**
 * Icon types
 */
export const METRIC_ICON_TYPE = {
  PLAIN: "plain",
  PILL: "pill",
} as const;

export const formatDate = (date?: Date) =>
  date
    ? date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
    : "—";

export const toastVariants = {
  default: "default",
  destructive: "destructive",
} as const;

export const Permissions = {
  accessPlayground: "access_playground",
  accessAgents: "access_agents",
  chatPersonas: "chat_with_personas",
  manageUsers: "manage_users",
  manageGroups: "manage_groups",
  manageOrgs: "manage_orgs",
  viewMetrics: "view_metrics",
  knowledgeStore: "knowledge_store",
  promptTemplates: "prompt_templates",
  settings: "settings",
  queryLibrary: "query_library",
  performanceDashboard: "performance_dashboard",
  userAdoptionInsights: "user_adoption_insights",
  feedbackReport: "feedback_report",
  promptGuidance: "prompt_guidance",
};

export const tableHeaders = {
  base: [
    "Record ID",
    "Input",
    "Output",
    "Latency",
    "Total Tokens",
    "Total Cost",
    "TimeStamp",
  ],
  recordID: "Record ID",
  input: "Input",
  output: "Output",
  latency: "Latency",
  totalTokens: "Total Tokens",
  totalCost: "Total Cost",
  timeStamp: "TimeStamp",
  organization: "Organization",
};

export const userSettingsMessages = {
  title: "User Profile",
  description: "Manage your account details",
  notifications: {
    title: "Notification Settings",
    email: "Email Notifications",
    browser: "Browser Notifications",
    mobile: "Mobile App Notifications",
  },
};

export const appearanceMessages = {
  description: "Customize how the application looks",
  theme: "Theme",
  language: "Language",
  fontSize: "Font Size",
  highContrast: "High Contrast",
  themeOptions: {
    dark: "Dark",
    light: "Light",
    system: "System",
  },
  languageOptions: {
    english: "English",
    french: "French",
    spanish: "Spanish",
    german: "German",
  },
  fontOptions: {
    small: "Small",
    medium: "Medium",
    large: "Large",
  },
};

export const aiSettingsMessages = {
  description: "Configure AI behavior and defaults",
  defaultModel: "Default AI Model",
  streamResponses: "Stream Responses",
  modelOptions: {
    gpt3point5: "GPT-3.5",
    gpt4: "GPT-4",
    claude2: "Claude 2",
    llama3: "LLaMA 3",
  },
};

export const MOBILE_BREAKPOINT = 768;

export const Routes = {
  dashboard: "/dashboard",
  chat: "/chat",
  agents: "/agents",
  users: "/users",
  groups: "/groups",
  organizations: "/organizations",
  metrics: "/metrics",
  knowledgeStore: "/knowledge-store",
  prompts: "/prompt-template",
  settings: "/settings",
  login: "/login",
  unAuthorized: "/unauthorized",
  prompt: "/prompts",
  queryLibrary: "/query-library",
  performanceDashboard: "/performance-dashboard",
  userAdoptionInsights: "/user-adoption-insights",
  feedbackReport: "/feedback-report",
  promptGuidance: "/prompt-guidance",
  explainability: "/explainability",
};

export const TOAST_LIMIT = 1;

export const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const;

export const fileNotSelected = "No files selected";

// constants.ts
export const InvertedMetrics = [
  "Criminality",
  "Maliciousness",
  "Harmfulness",
  "Insensitivity",
  "Misogyny",
  "Controversiality",
  "Biasness",
];

export const StatusLevels = {
  high: "high",
  medium: "medium",
  low: "low",
};

export const UI_LABELS = {
  knowledgeBasePlayground: "Knowledge Base Playground",
  testPlayground: "Test Playground",
  configureKB: "Configure Knowledge Base",
  selectKB: "Select KB",
  settings: "Settings",
  enterPrompt: "Enter your prompt",
  response: "Response",
  submitRequest: "Submit Request",
  addNewKB: "+ Add New Knowledge Base",
  temperature: "Temperature:",
  maxTokens: "Max Tokens:",
  enableCitations: "Enable citations",
  contextWindow: "Context Window:",
  moreFocused: "More Focused",
  moreCreative: "More Creative",
  shorter: "Shorter",
  longer: "Longer",
  shorterResponses: "Shorter Responses",
  longerResponses: "Longer Responses",
  lessContext: "Less Context",
  moreContext: "More Context",
  email: "Email",
  password: "Password",
  genaiWorkspace: "GenAI Studio",
  enterCredentials: "Enter your credentials to access your account",
  defaultMaxTokens: "Default Max Tokens:",
  moreDeterministic: "More Deterministic",
  defaultTemperature: "Default Temperature:",
  document: "document",
  webUrl: "web url",
};

export const allowedFileTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export enum MetricStatus {
  MONITORING = "monitoring",
  OPTIMAL = "optimal",
  HEALTHY = "healthy",
}

export enum BadgeVariant {
  DEFAULT = "default",
  SECONDARY = "secondary",
  DESTRUCTIVE = "destructive",
  OUTLINE = "outline",
}

export const modelProviderList = [
  { id: "google", name: "Google" },
  { id: "openai", name: "OpenAI" },
  { id: "azure", name: "Azure" },
  { id: "nvidia", name: "Nvidia" },
];

export const modelList = [
  { id: "gemini-1.5-flash", name: "gemini-1.5-flash", providerId: "google" },
  { id: "gemini-2.0-flash", name: "gemini-2.0-flash", providerId: "google" },
  { id: "gpt-4", name: "gpt-4o", providerId: "openai" },
  { id: "azure-gpt-4", name: "gpt-4o", providerId: "azure" },
  { id: "gpt-3.5-turbo", name: "gpt-3.5-turbo", providerId: "openai" },
  {
    id: "meta/llama3-8b-instruct",
    name: "meta/llama3-8b-instruct",
    providerId: "nvidia",
  },
  {
    id: "meta/llama-3.1-70b-instruct",
    name: "meta/llama-3.1-70b-instruct",
    providerId: "nvidia",
  },
];

export const RATING_COLORS: Record<string, string> = {
  veryGood: "text-green-600",
  good: "text-green-500",
  ok: "text-yellow-600",
  partialOk: "text-orange-600",
  notSatisfied: "text-red-600",
};

export const KNOWLEDGE_STATUS: Record<string, string> = {
  FAILED: "bg-red-100 text-red-700 border-red-200",
  PROCESSING: "bg-yellow-100 text-yellow-700 border-yellow-200",
  SUCCESS: "bg-green-100 text-green-700 border-green-200",
};

export const DEFAULT_SCREEN_ID = "APP_LAUNCH";
export const SESSION_WARNING_TIME_MS = 3 * 60 * 1000; // 3 minutes
export const TIMER_INTERVAL_MS = 1000;

export const SESSION_WARNING_TIME_SECONDS = 180; // 3 minutes

export const mockKnowledgeBases = [
  { id: 1, name: "Knowledge Base 1" },
  { id: 2, name: "Knowledge Base 2" },
  { id: 3, name: "Knowledge Base 3" },
];

export const mockPromptTemplates = [
  { id: 1, name: "Prompt Template 1" },
  { id: 2, name: "Prompt Template 2" },
  { id: 3, name: "Prompt Template 3" },
];

export const viewModes = {
  list: "list",
  create: "create",
  edit: "edit",
  versions: "versions",
  details: "details",
  templateEdit: "templateEdit",
  versionCreate: "versionCreate",
} as const;

export const response_types = {
  chunk: "chunk",
  fullResponse: "full_response",
  sources: "sources",
  followUpQuestions: "follow_up_questions",
  event: "event",
  metadata: "metadata",
  completed: "completed",
  error: "error",
  reasoning: "reasoning",
} as const;

export const streamStatusMap: Record<string, string> = {
  user_query_pii_masking: "labels.userQueryPiiMasking",
  semantic_cache_search: "labels.semanticCacheSearch",
  input_guardrails_validation: "labels.inputGuardrailsValidation",
  document_retrieval: "labels.documentRetrieval",
  chain_streaming_response: "labels.chainStreamingResponse",
  response_pii_masking: "labels.responsePiiMasking",
  output_guardrails_validation: "labels.outputGuardrailsValidation",
  generate_sources: "labels.generateSources",
  generate_follow_up_questions: "labels.generateFollowUpQuestions",
  prompt_template: "labels.promptTemplateMessage",
  completed: null,
};

export const KNOWLEDGE_FORM_FIELDS = {
  DOCUMENT: "document",
};

export const KNOWLEDGE_DEFAULT_PROVIDER = "Nvidia";
export const KNOWLEDGE_SOURCE_TYPE = "document";
export const KNOWLEDGE_REQUEST_KEYS = {
  CREATE: "add_knowledge_request",
  UPDATE: "update_knowledge_request",
};

export const KNOWLEDGE_FILE_ACCEPT = ".pdf,.doc,.docx";
export const SOURCE_TYPE = {
  DOCUMENT: "document",
  WEB: "web",
} as const;

export const LATENCY_QUERY_NAME = "chat_send_average_latency_ms";
export const THROUGHPUT_QUERY_NAME = "chat_send_requests_rate";
export const KNOWLEDGE_ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

export const allowedKeys = [
  "Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight",
  "ArrowUp", "ArrowDown", "Home", "End"
]